import {
  Bus, MapPin, Store, Phone, Globe, Smartphone,
  Info, Users, Accessibility, Smile,
  CheckCircle2, Wrench, Megaphone, Car, ShieldCheck,
  Eye, Clock, Sparkles, Zap, Layout, MessageCircle,
} from 'lucide-react';

/**
 * Category definitions for the macro-category toolbar and sidebar.
 * Each entry carries its map-element type, display label, icon, and colour tokens.
 */
export const CATEGORIES = [
  { id: 'linee_urbane',      label: 'Linee Urbane',      icon: Bus,        bg: '#90EFEF', fg: '#006E6E', bar: '#006A6A' },
  { id: 'linee_extraurbane', label: 'Linee Extraurbane', icon: Car,        bg: '#D9E9AD', fg: '#3E4C1E', bar: '#273308' },
  { id: 'punti_vendita',     label: 'Punti Vendita',     icon: Store,      bg: '#C3F400', fg: '#3C4D00', bar: '#9AC100' },
  { id: 'fermate',           label: 'Fermate',           icon: MapPin,     bg: '#D9E9AD', fg: '#3E4C1E', bar: '#566434' },
  { id: 'call_center',       label: 'Call Center',       icon: Phone,      bg: '#90EFEF', fg: '#006E6E', bar: '#006A6A' },
  { id: 'sito_web',          label: 'Sito Web',          icon: Globe,      bg: '#C3F400', fg: '#3C4D00', bar: '#9AC100' },
  { id: 'app',               label: 'App Mobile',        icon: Smartphone, bg: '#D9E9AD', fg: '#3E4C1E', bar: '#566434' },
];

/**
 * Icon map for sub-category breakdown items in the detail panels.
 * Covers both new-format keys and legacy keys from main_poject.
 */
export const SUB_CATEGORY_ICONS = {
  // New-format keys
  tipo_e_stato_fermata:         MapPin,
  visibilita:                   Eye,
  visibilita_e_riconoscibilita: Eye,
  sicurezza_e_accessibilita:    ShieldCheck,
  pulizia:                      ShieldCheck,
  pulizia_e_ordine:             ShieldCheck,
  informazioni_e_comunicazione: Info,
  operativita:                  Wrench,
  stato_del_mezzo:              CheckCircle2,
  comfort:                      Smile,
  informazioni:                 Info,
  comunicazione:                Megaphone,
  personale:                    Users,
  attesa:                       Clock,
  messaggeria:                  MessageCircle,
  contenuti:                    Layout,
  chiarezza_info:               Eye,
  funzioni:                     Zap,
  funzionalita:                 Zap,
  servizi:                      Store,
  user_experience:              Smartphone,
  // Legacy keys
  tipo_fermata:                 MapPin,
  sicurezza:                    ShieldCheck,
  accessibilita:                Accessibility,
  stato_mezzo:                  CheckCircle2,
  chiarezza:                    Eye,
  spazio:                       Store,
  odori:                        Sparkles,
  illuminazione:                Sparkles,
};

/**
 * City centre coordinates [lat, lng] used to centre the map.
 */
export const CITY_COORDS = {
  BERGAMO:  [45.6983, 9.6773],
  MESSINA:  [38.1931, 15.5552],
  CAGLIARI: [39.2238, 9.1217],
  LECCE:    [40.3515, 18.1750],
  SONDRIO:  [46.1677, 9.8677],
  BARI:     [41.1063, 16.8736],
  PALERMO:  [38.1157, 13.3615],
  MILANO:   [45.4642, 9.1900],
  ROMA:     [41.9028, 12.4964],
  TORINO:   [45.0703, 7.6869],
  BOLOGNA:  [44.4949, 11.3426],
  GENOVA:   [44.4056, 8.9463],
  COMO:     [45.8081, 9.0852],
  MODENA:           [44.64699449205872, 10.925164641908118],
  PIACENZA:          [45.051915348580664, 9.69334150035155],
  'REGGIO EMILIA':   [44.703818294579385, 10.63277435129133],
  'SAN DONA DI PIAVE': [45.6300, 12.5636],
};

/**
 * Zone GeoJSON file paths for real municipal boundaries.
 * Served from /public/zone/.
 */
export const ZONE_FILES = {
  BERGAMO:          '/zone/Bergamo_zona.geojson',
  CAGLIARI:         '/zone/Cagliari_zona.geojson',
  MESSINA:          '/zone/Messina_zona.geojson',
  LECCE:            '/zone/Lecce_zona.geojson',
  SONDRIO:          '/zone/Sondrio_zona.geojson',
  BARI:             '/zone/Bari_zona.geojson',
  MODENA:           '/zone/Modena_zona.geojson',
  PIACENZA:         '/zone/Piacenza_zona.geojson',
  'REGGIO EMILIA':  '/zone/Reggio_Emilia_zona.geojson',
};

/**
 * GTFS line geometry files per city.
 */
export const GTFS_FILES = {
  BERGAMO:  '/lines/bergamo_linee.geojson',
  CAGLIARI: '/lines/cagliari_linee.geojson',
  MESSINA:  '/lines/messina_linee.geojson',
};

/**
 * Fallback polygons for cities without zone GeoJSON.
 * Shown while real files load or when city has no zone file.
 */
export const CITY_POLYGONS = {
  MILANO: [
    [45.5340, 9.1260], [45.5360, 9.1790], [45.5210, 9.2370], [45.4830, 9.2540],
    [45.4330, 9.2410], [45.4100, 9.1870], [45.4080, 9.1270], [45.4430, 9.0860],
    [45.4940, 9.0800],
  ],
  ROMA: [
    [41.9980, 12.3600], [42.0060, 12.4220], [41.9900, 12.5010], [41.9590, 12.5510],
    [41.8790, 12.5600], [41.8100, 12.5210], [41.7790, 12.4610], [41.7900, 12.3820],
    [41.8410, 12.3210], [41.9310, 12.3110],
  ],
  TORINO: [
    [45.1250, 7.6280], [45.1310, 7.6760], [45.1190, 7.7210], [45.0840, 7.7470],
    [45.0370, 7.7210], [45.0030, 7.6730], [45.0090, 7.6210], [45.0550, 7.5910],
    [45.1010, 7.5890],
  ],
  BOLOGNA: [
    [44.5390, 11.2830], [44.5440, 11.3300], [44.5370, 11.3820], [44.5130, 11.4120],
    [44.4750, 11.4010], [44.4470, 11.3650], [44.4450, 11.3110], [44.4640, 11.2730],
    [44.5110, 11.2610],
  ],
  GENOVA: [
    [44.4690, 8.8230], [44.4730, 8.8840], [44.4710, 8.9520], [44.4690, 9.0230],
    [44.4510, 9.0820], [44.4010, 9.0510], [44.3910, 8.9810], [44.3960, 8.9210],
    [44.4210, 8.8710], [44.4410, 8.8210],
  ],
  MODENA: [
    [44.6850, 10.8910], [44.6890, 10.9320], [44.6840, 10.9720], [44.6610, 10.9970],
    [44.6310, 10.9910], [44.6110, 10.9510], [44.6110, 10.9120], [44.6310, 10.8810],
    [44.6610, 10.8760],
  ],
};

/**
 * Macro-category button definitions for the map toolbar.
 * mapType null = non-spatial category (no pins/lines on map).
 */
export const MACRO_BUTTONS = [
  { id: 'linee_urbane',  label: 'Linee',         icon: Bus,        mapType: 'linea' },
  { id: 'fermate',       label: 'Fermate',        icon: MapPin,     mapType: 'fermata' },
  { id: 'punti_vendita', label: 'Punti Vendita',  icon: Store,      mapType: 'punto_vendita' },
  { id: 'call_center',   label: 'Call Center',    icon: Phone,      mapType: null },
  { id: 'sito_web',      label: 'Sito Web',       icon: Globe,      mapType: null },
  { id: 'app',           label: 'App',            icon: Smartphone, mapType: null },
];

/**
 * All macro groups for DrilldownSidebar.
 */
export const SERVIZI_FISICI = [
  { id: 'linee_urbane',  label: 'Linee Urbane',  icon: Bus,    color: '#90EFEF' },
  { id: 'fermate',       label: 'Fermate',        icon: MapPin, color: '#A3E635' },
  { id: 'punti_vendita', label: 'Punti Vendita',  icon: Store,  color: '#C3F400' },
];

export const SERVIZI_DIGITALI = [
  { id: 'call_center', label: 'Call Center', icon: Phone,      color: '#F59E0B' },
  { id: 'sito_web',    label: 'Sito Web',    icon: Globe,      color: '#93C5FD' },
  { id: 'app',         label: 'App Mobile',  icon: Smartphone, color: '#A3E635' },
];

export const ALL_MACROS = [...SERVIZI_FISICI, ...SERVIZI_DIGITALI];

/**
 * Macro labels for display in panels.
 */
export const MACRO_LABELS = {
  linee_urbane:  'Linee Urbane',
  linee_extraurbane: 'Linee Extraurbane',
  fermate:       'Fermate',
  punti_vendita: 'Punti Vendita',
  call_center:   'Call Center',
  sito_web:      'Sito Web',
  app:           'App Mobile',
};
